import { NetworkStatus, useQuery } from "@apollo/client";
import { Search } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { Form, Formik, useFormikContext } from "formik";
import _ from "lodash";
import { ReactElement, ReactNode, useEffect } from "react";
import * as Yup from "yup";

import { DOMAIN_NAME_REGEX } from "@stw/yup-validators";

import {
  CheckLinkShortenerDomainNameAvailabilityQuery,
  CheckLinkShortenerDomainNameAvailabilityQueryVariables,
  CreateLinkShortenerDomainRequestMutationVariables,
  LinkShortenerDomainRequestType,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import FormikSwitchField from "~frontend/components/forms/FormikSwitchField";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { CHECK_LINK_SHORTENER_DOMAIN_AVAILABILITY } from "~frontend/graphql/domains";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useDebounce from "~frontend/util/useDebounce";

import {
  AVAILABLE_TLDS,
  DOMAIN_PART_REGEX,
  isValidDomainPart,
} from "./domainRequestValidation";

type Values = {
  [submitErrorField]: string | null;
  isExistingDomain: boolean;
  newDomainPart: string;
  newDomainTld: string;
  newDomainAvailable: boolean;
  domain: string;
  fallbackUrl: string;
};

function DomainSearchInput() {
  const { values, errors, touched, setFieldValue } = useFormikContext<Values>();

  const debouncedValue = useDebounce(values.newDomainPart, 500);
  const { data, networkStatus, variables } = useQuery<
    CheckLinkShortenerDomainNameAvailabilityQuery,
    CheckLinkShortenerDomainNameAvailabilityQueryVariables
  >(CHECK_LINK_SHORTENER_DOMAIN_AVAILABILITY, {
    variables: {
      input: {
        domain: `${debouncedValue}${values.newDomainTld}`,
      },
    },
    notifyOnNetworkStatusChange: true,
    skip: debouncedValue.length < 2 || !isValidDomainPart(debouncedValue),
    fetchPolicy: "network-only",
  });

  let validationStatus:
    | "idle"
    | "loading"
    | "available"
    | "unavailable"
    | "error" = "idle";

  if (
    networkStatus === NetworkStatus.loading ||
    networkStatus == NetworkStatus.setVariables ||
    values.newDomainPart !== debouncedValue
  ) {
    validationStatus = "loading";
  } else if (networkStatus == NetworkStatus.ready && data) {
    if (data.checkLinkShortenerDomainNameAvailability) {
      validationStatus = "available";
    } else {
      validationStatus = "unavailable";
    }
  } else if (networkStatus === NetworkStatus.error) {
    validationStatus = "error";
  }

  let message: ReactNode = null;
  if (errors.newDomainPart) {
    // Formik validation error
    message = errors.newDomainPart;
  } else if (validationStatus === "loading") {
    message = "Checking availability...";
  } else if (validationStatus === "available") {
    message = (
      <Typography display="inline" color="success">
        ✔ {variables?.input.domain} is available!
      </Typography>
    );
  } else if (validationStatus === "unavailable") {
    message = (
      <Typography display="inline" color="error">
        {variables?.input.domain} is not available
      </Typography>
    );
  } else if (validationStatus === "error") {
    message = (
      <Typography display="inline" color="error">
        Error checking availability
      </Typography>
    );
  }

  useEffect(() => {
    setFieldValue("newDomainAvailable", validationStatus === "available").catch(
      console.error
    );
  }, [setFieldValue, validationStatus]);

  return (
    <FlexBox>
      <Box sx={{ flex: 1 }}>
        <FormikTextField
          name="newDomainPart"
          label="New Domain Name"
          fullWidth
          value={values.isExistingDomain ? "" : values.newDomainPart}
          error={
            !values.isExistingDomain &&
            touched.newDomainPart &&
            (!!errors.newDomainPart ||
              validationStatus === "unavailable" ||
              validationStatus == "error")
          }
          helperText={values.isExistingDomain ? "" : message}
          disabled={values.isExistingDomain}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 1 }}>
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ width: "200px" }}>
        <FormikSelect
          fullWidth
          name="newDomainTld"
          disabled={values.isExistingDomain}
        >
          {AVAILABLE_TLDS.map(tld => (
            <MenuItem key={tld} value={tld}>
              {tld}
            </MenuItem>
          ))}
        </FormikSelect>
      </Box>
    </FlexBox>
  );
}

export default function AddDomainDialog({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CreateLinkShortenerDomainRequestMutationVariables) => void;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Request A Link Shortener Domain</DialogTitle>
      <Formik<Values>
        initialValues={{
          [submitErrorField]: null,
          newDomainAvailable: false,
          isExistingDomain: false,
          newDomainPart: "",
          newDomainTld: ".com",
          domain: "",
          fallbackUrl: "",
        }}
        validationSchema={Yup.object().shape({
          domain: Yup.string()
            .optional()
            .when("isExistingDomain", {
              is: true,
              then: Yup.string()
                .required("Required")
                .matches(DOMAIN_NAME_REGEX, "Must be a valid domain name"),
            }),
          newDomainPart: Yup.string()
            .optional()
            .when("isExistingDomain", {
              is: false,
              then: Yup.string()
                .required("Required")
                .matches(DOMAIN_PART_REGEX, "Must be a valid domain name"),
            }),
          fallbackUrl: Yup.string()
            .required("Required")
            .url("Must be a valid URL"),
        })}
        onSubmit={async (values, formik) => {
          const input = values.isExistingDomain
            ? {
                requestType: LinkShortenerDomainRequestType.Existing,
                domain: values.domain,
                fallbackUrl: values.fallbackUrl,
              }
            : {
                requestType: LinkShortenerDomainRequestType.New,
                domain: `${values.newDomainPart}${values.newDomainTld}`,
                fallbackUrl: values.fallbackUrl,
              };

          try {
            await onSubmit({
              input,
            });
            formik.resetForm();
          } catch (e) {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(e),
            });
          }
        }}
      >
        {({ isSubmitting, values, resetForm }) => (
          <Form>
            <DialogContent sx={{ pt: 0 }}>
              <FormikErrorBanner />
              <Typography sx={{ mb: 2 }}>
                Use the search bar below to look for an available domain. Once
                you have found the domain you want, click the submit button
                below.{" "}
                <strong>
                  Your domain should be available for use within 3 to 5 business
                  days.
                </strong>
              </Typography>

              <DomainSearchInput />

              <FormikSwitchField
                name="isExistingDomain"
                label="I want to use a custom domain or subdomain that I already own"
              />

              {values.isExistingDomain && (
                <>
                  <Typography sx={{ mt: 4, mb: 2 }}>
                    If you already own a custom domain or subdomain that
                    you&apos;d like to use here, submit the domain below and a
                    member of the Scale to Win team will reach out to coordinate
                    getting your domain running on our platform. This must be a
                    domain or subdomain that you own, can configure DNS settings
                    for, and is not being used for any other purpose.
                  </Typography>
                  <FormikTextField
                    name="domain"
                    label="Owned Domain Name"
                    fullWidth
                  />
                </>
              )}

              <Typography sx={{ mt: 4 }}>
                Add a fallback URL that users will be sent to if they go
                directly to your domain, or to a broken link (this is usually
                your organization's homepage URL, and can be changed later if
                needed).
              </Typography>
              <FormikTextField
                name="fallbackUrl"
                label="Fallback URL"
                fullWidth
              />
            </DialogContent>
            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                color="secondary"
              >
                Cancel
              </Button>

              <FormikSubmitButton
                color="primary"
                variant="contained"
                submitText="Request Domain"
                loadingText="Requesting..."
                disabled={
                  !values.isExistingDomain && !values.newDomainAvailable
                }
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
